import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Link,
  Divider,
  Grid,
} from '@mui/material';
import { Skill, SkillNode, SkillResource, WorkAssignmentWithScore, LessonFile } from '../types/microsoftGraph';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`skill-tabpanel-${index}`}
      aria-labelledby={`skill-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface SkillDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  skillDetails: Skill | null;
  skillNodes: SkillNode[];
  readOnly?: boolean;
  workAssignments?: WorkAssignmentWithScore[];
  resources?: LessonFile[];
  onTabChange?: (event: React.SyntheticEvent, newValue: number) => void;
}

const SkillDetailsDialog: React.FC<SkillDetailsDialogProps> = ({
  open,
  onClose,
  skillDetails,
  skillNodes,
  readOnly = false,
  workAssignments = [],
  resources = [],
  onTabChange
}) => {

  const [selectedTab, setSelectedTab] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    if (onTabChange) {
      onTabChange(event, newValue);
    }
  };

  if (!skillDetails) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  // Helper to get skill name by ID
  const getSkillName = (skillID: number): string => {
    const skill = skillNodes.find(s => s.SkillID === skillID);
    return skill ? skill.SkillName : `Skill ${skillID}`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
          {skillDetails.skillName}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Tabs value={selectedTab} onChange={handleTabChange} aria-label="skill details tabs">
          <Tab label="Description & Criteria" />
          {/* ✅ FIX: Only disable if truly read-only AND no work assignments */}
          <Tab label="My Progress" disabled={readOnly && workAssignments.length === 0} />
          <Tab label="Resources" />
        </Tabs>

        {/* Tab 0: Skill Description & Criteria */}
        <TabPanel value={selectedTab} index={0}>
          <Grid container spacing={3}>
            {/* Skill Description */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                Skill Description
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                <Typography variant="body1">
                  {skillDetails.skillDescription || 'No description available.'}
                </Typography>
              </Paper>
            </Grid>

            {/* Development Age (if available) */}
            {skillDetails.developmentAge && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  <strong>Typical Development Age:</strong> {skillDetails.developmentAge}
                </Typography>
              </Grid>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {/* Proficiency Criteria */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                Proficiency Criteria
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Performance descriptors for each proficiency level (1-5)
              </Typography>
            </Grid>

            {/* Criteria 1: Beginning */}
            {skillDetails.criteria1 && (
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Chip 
                      label="Level 1" 
                      size="small" 
                      sx={{ 
                        bgcolor: '#F44336', 
                        color: '#fff', 
                        fontWeight: 'bold',
                        mr: 1 
                      }} 
                    />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      Beginning
                    </Typography>
                  </Box>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#ffebee' }}>
                    <Typography variant="body2">{skillDetails.criteria1}</Typography>
                  </Paper>
                </Box>
              </Grid>
            )}

            {/* Criteria 2: Emerging */}
            {skillDetails.criteria2 && (
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Chip 
                      label="Level 2" 
                      size="small" 
                      sx={{ 
                        bgcolor: '#E29A3E', 
                        color: '#fff', 
                        fontWeight: 'bold',
                        mr: 1 
                      }} 
                    />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      Emerging
                    </Typography>
                  </Box>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fff3e0' }}>
                    <Typography variant="body2">{skillDetails.criteria2}</Typography>
                  </Paper>
                </Box>
              </Grid>
            )}

            {/* Criteria 3: Developing */}
            {skillDetails.criteria3 && (
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Chip 
                      label="Level 3" 
                      size="small" 
                      sx={{ 
                        bgcolor: '#C9D144', 
                        color: '#fff', 
                        fontWeight: 'bold',
                        mr: 1 
                      }} 
                    />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      Developing
                    </Typography>
                  </Box>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f9fbe7' }}>
                    <Typography variant="body2">{skillDetails.criteria3}</Typography>
                  </Paper>
                </Box>
              </Grid>
            )}

            {/* Criteria 4: Proficient */}
            {skillDetails.criteria4 && (
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Chip 
                      label="Level 4" 
                      size="small" 
                      sx={{ 
                        bgcolor: '#7FC049', 
                        color: '#fff', 
                        fontWeight: 'bold',
                        mr: 1 
                      }} 
                    />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      Proficient
                    </Typography>
                  </Box>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f1f8e9' }}>
                    <Typography variant="body2">{skillDetails.criteria4}</Typography>
                  </Paper>
                </Box>
              </Grid>
            )}

            {/* Criteria 5: Mastered */}
            {skillDetails.criteria5 && (
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Chip 
                      label="Level 5" 
                      size="small" 
                      sx={{ 
                        bgcolor: '#4CAF50', 
                        color: '#fff', 
                        fontWeight: 'bold',
                        mr: 1 
                      }} 
                    />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      Mastered
                    </Typography>
                  </Box>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#e8f5e9' }}>
                    <Typography variant="body2">{skillDetails.criteria5}</Typography>
                  </Paper>
                </Box>
              </Grid>
            )}

            {/* Skill Relationships */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {/* Parent Skills */}
            {skillDetails.parentSkills && skillDetails.parentSkills.length > 0 && (
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Prerequisites
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  {skillDetails.parentSkills.map((parent) => (
                    <Chip
                      key={parent.skillID}
                      label={parent.skillName}
                      size="small"
                      sx={{ m: 0.5 }}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Paper>
              </Grid>
            )}

            {/* Offspring Skills */}
            {skillDetails.offspringSkills && skillDetails.offspringSkills.length > 0 && (
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Leads To
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  {skillDetails.offspringSkills.map((offspring) => (
                    <Chip
                      key={offspring.skillID}
                      label={offspring.skillName}
                      size="small"
                      sx={{ m: 0.5 }}
                      color="secondary"
                      variant="outlined"
                    />
                  ))}
                </Paper>
              </Grid>
            )}
          </Grid>
        </TabPanel>

        {/* Tab 1: Work Assignments (Student Progress) */}
        <TabPanel value={selectedTab} index={1}>
          {workAssignments.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No work assignments found for this skill.
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="h6" gutterBottom>
                My Work Attempts ({workAssignments.length})
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Skill proficiency is measured on a scale of 1-5
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Assignment</TableCell>
                      <TableCell>File</TableCell>
                      <TableCell align="center">Skill Proficiency</TableCell>
                      <TableCell align="center">Date Completed</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {workAssignments.map((assignment) => {
                      // ✅ Determine proficiency level color based on 1-5 scale
                      const getProficiencyColor = (mark: number) => {
                        if (mark >= 5) return '#4CAF50'; // Green - Mastered
                        if (mark >= 4) return '#7FC049'; // Light Green - Proficient
                        if (mark >= 3) return '#C9D144'; // Yellow-Green - Developing
                        if (mark >= 2) return '#E29A3E'; // Orange - Emerging
                        return '#F44336'; // Red - Beginning
                      };

                      const getProficiencyLabel = (mark: number) => {
                        if (mark >= 5) return 'Mastered';
                        if (mark >= 4) return 'Proficient';
                        if (mark >= 3) return 'Developing';
                        if (mark >= 2) return 'Emerging';
                        return 'Beginning';
                      };

                      return (
                        <TableRow key={assignment.assignmentID}>
                          <TableCell>{assignment.assignmentName}</TableCell>
                          <TableCell>
                            <Typography variant="caption">{assignment.fileName}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                              <Chip
                                label={`${assignment.score}/5`}
                                size="small"
                                sx={{
                                  bgcolor: getProficiencyColor(assignment.score),
                                  color: '#fff',
                                  fontWeight: 'bold'
                                }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {getProficiencyLabel(assignment.score)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            {assignment.dateCompleted
                              ? new Date(assignment.dateCompleted * 1000).toLocaleDateString()
                              : '-'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </TabPanel>

{/* Tab 2: Learning Resources */}
<TabPanel value={selectedTab} index={2}>
  {!resources || resources.length === 0 ? (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <Typography variant="body1" color="text.secondary">
        No learning resources available for this skill yet.
      </Typography>
    </Box>
  ) : (
    <>
      <Typography variant="h6" gutterBottom>
        Learning Resources ({resources.length})
      </Typography>
      <Grid container spacing={2}>
        {resources.map((file, index) => {
          // ✅ Determine resource type for display
          const isVideo = file.fileType?.toLowerCase().includes('video') || 
                         file.cloudId?.startsWith('yt-');
          const isDocument = file.fileType?.toLowerCase().includes('document') || 
                            file.fileType?.toLowerCase().includes('pdf');
          
          return (
            <Grid item xs={12} key={`lesson-file-${file.lessonFilesId}-${index}`}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {file.filename}
                    </Typography>
                    {file.fileType && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {file.fileType}
                      </Typography>
                    )}
                    <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip 
                        label={file.fileType || 'File'} 
                        size="small" 
                        color={isVideo ? 'error' : isDocument ? 'primary' : 'default'}
                      />
                      {file.timeReq > 0 && (
                        <Chip 
                          label={`${file.timeReq} min`} 
                          size="small" 
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Box>
                  {file.cloudId && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {isVideo && file.cloudId.startsWith('yt-') ? (
                        // YouTube video - open in dialog or new tab
                        <Link 
                          href={`https://youtube.com/watch?v=${file.cloudId.replace('yt-', '')}`}
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          <Button variant="outlined" size="small" color="error">
                            Watch Video
                          </Button>
                        </Link>
                      ) : (
                        // Regular file - open in new tab
                        <Link 
                          href={`/files/${file.cloudId}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          <Button variant="outlined" size="small">
                            Open
                          </Button>
                        </Link>
                      )}
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </>
  )}
</TabPanel>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SkillDetailsDialog;